import {
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Stack,
  Strong,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Field, FieldProps, Form, FormikProps, useFormikContext } from "formik";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ContactField from "./FormComponents/ContactField";
import FormikSelectField from "./FormComponents/FormikSelectField";
import GeneralTextField from "./FormComponents/GeneralTextField";
import {
  AccountPayer,
  DocumentTypeEnum,
  FormValues,
  GasOptionEnum,
} from "./types";
import {
  formGasMeterHomeUserValidationSchema,
  formGasMeterWithCompanyValidationSchema,
} from "./FormComponents/Validations";
import { apiClient } from "src/api/apiService";
import Calculator from "./FormComponents/Calculator";
import FormContext from "src/context/FormContext";
import {
  gasMeterTimeOptions,
  getNestedValue,
  hasTotalLoadDetails,
  isUnavailableDateForThreeWorkingDays,
  meterLocationOptions,
  populateTotalLoadSummary,
  pressureOptions,
  preSubmissionRequestUpdate,
  sendEicComms,
  showUpdatePremiseAlert,
  siteConditionsOptions,
  submitBlock,
  validateAddress,
  validateCseNumber,
  validatePlumberLicense,
} from "./helper";
import DatePickerField from "./FormComponents/DatePickerField";
import { useNavigate } from "react-router-dom";
import { getErrorTypeEnum } from "../../api/apiHelper";
import ResidentialGasLoadField from "./FormComponents/ResidentialGasLoadField";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import HomeUserLicencedPlumber from "./HomeUser/HomeUserLicencedPlumber";
import * as Yup from "yup";
import { useAppContext } from "src/context/AppContext";
import { EICStatusEnum } from "../Premises/types";
import { usePermissions } from "../../hooks/usePermissions";
import { LifeSupportSection } from "./FormComponents/LifeSupportSection";
import PermissionsActionButtons from "./PermissionsActionButtons";
import { NewPremiseFormFileUploadFormikField } from "./NewPremise/NewPremiseFormFileUploadFormikField";

interface GasMeterBlockProps {
  setFormLoadValues?: (values: FormValues) => void;
  homeUser: boolean;
  premiseId: string | null;
}

const GasMeterBlock: React.FC<GasMeterBlockProps> = ({
  setFormLoadValues,
  homeUser,
  premiseId,
}) => {
  const [showStreetNumber, setShowStreetNumber] = useState(false);
  const navigate = useNavigate();
  const {
    formState: { gasServices, isAccountReadOnly },
    setFormState,
    setDisplayingSnackbar,
    setDisplayingAlertBanner,
  } = useContext(FormContext);
  const {
    context: { user },
  } = useAppContext();
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();
  const [savingBlock, setSavingBlock] = useState(false);
  const { values, setFieldValue, setErrors, errors } =
    useFormikContext<FormValues>();
  const {
    propertyType,
    premiseAddress: { state },
    gasMeter: { pressure, siteConditions, meterLocation },
  } = values;
  const [open, setOpen] = useState(false);

  const displayLicensedPlumber =
    values.premiseAddress?.state === "VIC" ||
    values.premiseAddress?.state === "NSW" ||
    values.premiseAddress?.state === "SA" ||
    values.premiseAddress?.state === "QLD";

  useEffect(() => {
    if (!values.premiseAddress.streetNumber) {
      setShowStreetNumber(true);
    }
  }, [gasServices?.gasLine?.draftId]);

  //cleaning up data from unused form pathways
  useEffect(() => {
    if (!siteConditions.includes("other")) {
      setFieldValue("gasMeter.otherSiteCondition", "");
    }
  }, [siteConditions]);

  useEffect(() => {
    if (pressure !== "other" && pressure !== "0") {
      setFieldValue("gasMeter.otherPressure", "");
    }
  }, [pressure]);

  useEffect(() => {
    if (meterLocation !== "other") {
      setFieldValue("gasMeter.otherMeterLocation", "");
    }
  }, [meterLocation]);

  const closeDialog = () => {
    setOpen(false);
  };
  const openDialog = () => {
    setOpen(true);
  };

  const saveGasMeterBlock = async () => {
    try {
      setSavingBlock(true);
      const manipulatedValues =
        isHomeUserFromPermissions &&
        values.accountHolder === "person" &&
        !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
        user
          ? {
              ...values,
              accountPrimaryEmail: user.email,
              accountPrimaryFirstName: user.first_name,
              accountPrimaryLastName: user.last_name,
            }
          : values;
      await preSubmissionRequestUpdate(
        manipulatedValues,
        gasServices!,
        setFormLoadValues!,
        setFormState,
        "gasMeter",
        undefined,
        undefined,
        false,
        homeUser
      );

      setDisplayingSnackbar({
        message: "Gas block updated",
        tone: "positive",
      });
      setDisplayingAlertBanner(null);
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to save gas block",
        tone: "caution",
      });
    } finally {
      setSavingBlock(false);
    }
  };

  const siteConditionsError = errors?.gasMeter?.siteConditions;
  const showCocNumberField = state === "VIC";
  const showSwnNumberField =
    state === "NSW" &&
    [
      "3644",
      "2640",
      "2641",
      "2642",
      "2643",
      "2647",
      "2712",
      "2713",
      "2714",
      "2731",
    ].includes(values.premiseAddress.postcode);

  const isDigitalEic =
    homeUser ||
    (values.gasMeter.accountPayer === AccountPayer.SOMEONE_ELSE &&
      values.accountHolderDetailsType === "online");

  const sendEicCommsOnLodge = async () => {
    if (
      isDigitalEic &&
      values.gasMeter.eicStatus !== EICStatusEnum.EIC_ACCEPTED
    ) {
      const { valid, message } = await sendEicComms(
        values.id,
        gasServices?.gasMeter?.requestId ?? null,
        homeUser && accountPayers.includes(AccountPayer.SOMEONE_ELSE) === false
          ? user?.first_name || ""
          : values.accountPrimaryFirstName,
        homeUser && accountPayers.includes(AccountPayer.SOMEONE_ELSE) === false
          ? user?.last_name || ""
          : values.accountPrimaryLastName,
        homeUser && accountPayers.includes(AccountPayer.SOMEONE_ELSE) === false
          ? user?.email || ""
          : values.accountPrimaryEmail,
        "Notification re-sent.",
        "Account failed to save."
      );
      if (valid) {
        setDisplayingAlertBanner(null);
        navigate("/", {
          state: {
            submitted: true,
            isDigitalEic,
            snackbarMessage: message,
          },
        });
      } else {
        setDisplayingSnackbar({
          message,
          tone: valid ? "positive" : "caution",
        });
      }
    }
  };

  const lodgeRequest = async () => {
    try {
      //if VIC, validate address and cseNumber
      let validAddress = false;
      let validCseNumber = false;
      if (values.premiseAddress.state === "VIC") {
        //1. validate address
        const { valid: _validAddress } = await validateAddress({
          streetName: values.premiseAddress.streetName,
          streetType: values.premiseAddress.streetType,
          streetSuffix: values.premiseAddress.streetSuffix,
          suburb: values.premiseAddress.suburb,
          postcode: values.premiseAddress.postcode,
        });
        validAddress = _validAddress;
        //2. validate cseNumber
        const licensedPlumber: string =
          getNestedValue(values, "gasMeter.licensedPlumber") ?? "";
        const licenseNumber = values.licensedPlumbers.find(
          (c) => c.id === licensedPlumber
        )?.license_number;
        if (licenseNumber) {
          const { valid: _validCseNumber } = await validateCseNumber(
            licenseNumber,
            values.gasMeter.cocNumber,
            values.gasMeter.swnNumber
          );
          validCseNumber = _validCseNumber;
        }
      }
      const manipulatedValues =
        homeUser &&
        values.accountHolder === "person" &&
        !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
        user
          ? {
              ...values,
              accountPrimaryEmail: user.email,
              accountPrimaryFirstName: user.first_name,
              accountPrimaryLastName: user.last_name,
            }
          : values;
      await preSubmissionRequestUpdate(
        manipulatedValues,
        gasServices!,
        setFormLoadValues!,
        setFormState,
        "gasMeter",
        state === "VIC"
          ? {
              [GasOptionEnum.GAS_METER]: {
                validPlumberLicence: true,
                validAddress,
                validCseNumber,
              },
            }
          : {},
        undefined,
        isDigitalEic,
        homeUser
      );
      if (isDigitalEic) {
        sendEicCommsOnLodge();
        return;
      }
      const { data } = await apiClient.put(
        `/submission/${gasServices?.gasMeter?.requestId}`
      );
      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
          journeyId: data.services[0].journey_id,
        },
      });
    } catch (error) {
      setDisplayingAlertBanner(getErrorTypeEnum(error) ?? null);
    }
  };

  const appliancesChecklistHelpText = (
    <>
      <Text>Please download and complete </Text>
      <TextLink
        href={
          "https://www.originenergy.com.au/content/dam/origin/residential/docs/elec-gas/all-appliance-checklist-act-nsw.pdf"
        }
        target="_blank"
      >
        <Strong>Natural gas appliances checklist</Strong>
      </TextLink>
      <Text>PDF form.</Text>
    </>
  );

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "gasMeterBlock",
    Boolean(premiseId)
  );

  return (
    <>
      {showUpdatePremiseAlert(
        accountPayers,
        Boolean(values.accountId),
        Boolean(premiseId)
      ) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Form>
          <Stack
            space={{
              xs: "xxlarge",
              md: "xxxlarge",
            }}
          >
            {/* Form container */}
            <Stack
              space={{
                xs: "xlarge",
                md: "xxlarge",
              }}
            >
              {showStreetNumber && (
                <FormFieldLayout>
                  <Field
                    component={GeneralTextField}
                    name="premiseAddress.streetNumber"
                    label="Street No."
                    maxLength={200}
                  />
                </FormFieldLayout>
              )}

              {homeUser && displayLicensedPlumber ? (
                <HomeUserLicencedPlumber type="gasMeter" />
              ) : (
                <FormFieldLayout>
                  <ContactField
                    contactType="PLUMBER"
                    name="gasMeter.licensedPlumber"
                    label="Licensed plumber"
                    validatePlumberLicense={validatePlumberLicense}
                  />
                </FormFieldLayout>
              )}

              {state === "SA" && (
                <Stack
                  space={{
                    xs: "xlarge",
                    md: "xxlarge",
                  }}
                >
                  <FormFieldLayout text="MIRN (Meter Installation Registration Number) can be obtained from the gas distributor, Australian Gas Networks.">
                    <Field
                      component={GeneralTextField}
                      name="gasMeter.mirn"
                      label="MIRN"
                      maxLength={10}
                    />
                  </FormFieldLayout>

                  <FormFieldLayout text="The date should be a minimum of 3 days from the current date.">
                    <Columns
                      collapseBelow="xl"
                      space={{
                        xs: "xlarge",
                        md: "xxlarge",
                        xl: "medium",
                      }}
                    >
                      <Column>
                        <DatePickerField
                          name="gasMeter.preferDate"
                          label="Preferred connection date"
                          shouldDisableDate={
                            isUnavailableDateForThreeWorkingDays
                          }
                        />
                      </Column>
                      <Column>
                        <Field
                          name="gasMeter.preferTime"
                          label="Preferred connection time"
                          component={FormikSelectField}
                          options={gasMeterTimeOptions}
                        />
                      </Column>
                    </Columns>
                  </FormFieldLayout>
                </Stack>
              )}
              {showCocNumberField && (
                <FormFieldLayout
                  text={
                    "Certificate of compliance or Energy safe Victoria number can be obtained from the licensed plumber"
                  }
                >
                  <Field
                    component={GeneralTextField}
                    name="gasMeter.cocNumber"
                    label="CoC or ESV number"
                    maxLength={200}
                    type="number"
                  />
                </FormFieldLayout>
              )}
              {showSwnNumberField && (
                <FormFieldLayout
                  text={
                    "Start works notice number can be obtained from the licensed plumber"
                  }
                >
                  <Field
                    component={GeneralTextField}
                    name="gasMeter.swnNumber"
                    label="SWN number"
                    maxLength={200}
                    type="number"
                  />
                </FormFieldLayout>
              )}
              {/* Copy gas line */}
              {/* {isGasLineExist && (
            <FormFieldLayout>
              <Field name="gasMeter.isUpdatesOnGasLine">
                {({ field }: { field: FormikProps<FieldProps> }) => (
                  <RadioGroup
                    {...field}
                    id="is-updates-on-gas-line"
                    name="gasMeter.isUpdatesOnGasLine"
                    label={`Any changes to gas requirements or site conditions since the ${
                      state === "NSW" ? "path valve" : "gas line"
                    } request?`}
                  >
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      flexDirection={{
                        xs: "column",
                        sm: "row",
                      }}
                    >
                      <Box marginRight="medium">
                        <Radio
                          id="yes"
                          label="Yes (provide update)"
                          value="yes"
                        />
                      </Box>
                      <Radio id="no" label="No changes" value="no" />
                    </Box>
                  </RadioGroup>
                )}
              </Field>
            </FormFieldLayout>
          )} */}
              {/* End Copy gas line */}
              {/* {gasMeter.isUpdatesOnGasLine === "yes" ||
          !gasServices?.gasLine?.draftId ? ( */}
              {/* <Stack
            space={{
              xs: "xlarge",
              md: "xxlarge",
            }}
          > */}
              <FormFieldLayout multipleFields>
                <Field
                  name="gasMeter.pressure"
                  label="Pressure"
                  component={FormikSelectField}
                  options={pressureOptions}
                />
                {/* Other pressure: "0" to represent "Other" */}
                {values.gasMeter.pressure === "0" && (
                  <Field
                    component={GeneralTextField}
                    name="gasMeter.otherPressure"
                    label="Other pressure value (kPa)"
                    maxLength={200}
                  />
                )}
              </FormFieldLayout>
              {/* End Pressure */}

              {/* Total Load Calculator */}
              {propertyType === "RESIDENTIAL" && (
                <FormFieldLayout>
                  <Stack space="small">
                    <ResidentialGasLoadField
                      name="gasMeter.totalLoad"
                      label="Total load, MJ/h"
                      openDialog={openDialog}
                    />
                    {hasTotalLoadDetails(values.gasMeter.totalLoadDetails) && (
                      <Text variant="body-small" tone="neutralLight">
                        {populateTotalLoadSummary(
                          values.gasMeter.totalLoadDetails
                        )}
                      </Text>
                    )}
                  </Stack>
                </FormFieldLayout>
              )}

              {/* Appliances Checklist */}
              {propertyType === "COMMERCIAL" && (
                <Stack space="xxlarge">
                  <FormFieldLayout
                    multipleFields
                    text={appliancesChecklistHelpText}
                  >
                    <Text>Appliances checklist</Text>
                    <Field name="gasMeter.appliancesChecklistFiles">
                      {({ field }: FieldProps) => (
                        <NewPremiseFormFileUploadFormikField
                          draftId={gasServices?.gasMeter?.draftId ?? ""}
                          documentTypes={[DocumentTypeEnum.APPLIANCE_CHECKLIST]}
                          field={field}
                        />
                      )}
                    </Field>
                  </FormFieldLayout>
                  <FormFieldLayout
                    multipleFields
                    text="Please ensure it's the same as in the uploaded appliances checklist to avoid unnecessary delays."
                  >
                    <Field
                      component={GeneralTextField}
                      name="gasMeter.totalLoad"
                      label="Total load, MJ/h"
                      maxLength={200}
                      type="number"
                    />
                  </FormFieldLayout>
                </Stack>
              )}
              {/* End Appliances Checklist */}

              {/* Total load */}

              {["NSW", "ACT"].includes(state) && (
                <FormFieldLayout multipleFields>
                  <CheckboxGroup
                    id="site-conditions"
                    label="Site conditions:"
                    error={Boolean(siteConditionsError)}
                    helperText={siteConditionsError?.toString()}
                  >
                    {siteConditionsOptions.map((option) => (
                      <Field
                        type="checkbox"
                        value={option.value}
                        name="gasMeter.siteConditions"
                        key={option.value}
                      >
                        {({ field }: { field: FormikProps<FieldProps> }) => (
                          <Checkbox
                            {...field}
                            key={option.value}
                            id={option.value}
                            label={option.label}
                          />
                        )}
                      </Field>
                    ))}
                  </CheckboxGroup>
                  {values.gasMeter.siteConditions.includes("other") && (
                    <Field
                      component={GeneralTextField}
                      name="gasMeter.otherSiteCondition"
                      label="Other site condition"
                      maxLength={30}
                    />
                  )}
                </FormFieldLayout>
              )}
              {["NSW", "ACT"].includes(state) && (
                <FormFieldLayout multipleFields>
                  <Field
                    name="gasMeter.meterLocation"
                    label="Meter location"
                    component={FormikSelectField}
                    options={meterLocationOptions}
                  />
                  {values.gasMeter.meterLocation === "other" && (
                    <Field
                      component={GeneralTextField}
                      name="gasMeter.otherMeterLocation"
                      label="Other meter location"
                      maxLength={200}
                    />
                  )}
                </FormFieldLayout>
              )}

              {/* Site Plan */}
              <FormFieldLayout multipleFields>
                <Text>Site plan</Text>
                <Field name="gasMeter.sitePlanFiles">
                  {({ field }: FieldProps) => (
                    <NewPremiseFormFileUploadFormikField
                      draftId={gasServices?.gasMeter?.draftId ?? ""}
                      documentTypes={[DocumentTypeEnum.SITE_PLAN]}
                      field={field}
                    />
                  )}
                </Field>
              </FormFieldLayout>
              {/* End Site Plan */}
            </Stack>
            {/* End Form container */}
            {open && (
              <Calculator
                appliancesFromFormik={values.gasMeter.totalLoadDetails}
                totalLoadFromFormik={values.gasMeter.totalLoad}
                closeDialog={closeDialog}
                setFieldValue={setFieldValue}
                gasOptionName={GasOptionEnum.GAS_METER}
              />
            )}

            <LifeSupportSection
              showPresentation={
                values.gasMeter?.accountPayer === AccountPayer.COMPANY &&
                !homeUser
              }
              showWithFormFieldLayout={true}
              fieldProps={{
                name: "gasMeter.lifeSupport",
              }}
            />
            {/* Actions */}
            <PermissionsActionButtons
              primaryOnClick={() =>
                submitBlock({
                  values,
                  setErrors,
                  validationSchema: (homeUser
                    ? formGasMeterHomeUserValidationSchema(
                        isAccountReadOnly,
                        isDigitalEic
                      )
                    : formGasMeterWithCompanyValidationSchema(
                        isAccountReadOnly,
                        isDigitalEic
                      )) as Yup.ObjectSchema<any>,
                  context: {
                    state,
                    propertyType,
                    showCocNumberField,
                    showSwnNumberField,
                    gasMeter: values.gasMeter,
                  },
                  gasOption: "gasMeter",
                  lodgeRequest,
                })
              }
              secondaryOnClick={saveGasMeterBlock}
              secondaryDisabled={gasServices?.gasMeter?.autoSubmit}
              secondarySpinner={savingBlock}
            />
            {/* End Actions */}
          </Stack>
        </Form>
      )}
    </>
  );
};

export default GasMeterBlock;
